import React from 'react';
import { experiences, upickHarvestImages, greenhouseGalleryImages, gardenBeautyImages, farmMomentsImages } from '../data/mock';
import { Leaf, Sprout, Apple, Flower2, BookOpen } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

const iconMap = {
  Leaf,
  Sprout,
  Apple,
  Flower2,
  BookOpen
};

// Experience card with integrated carousel gallery
const ExperienceCardWithGallery = ({ experience, galleryImages, fullHeight = false }) => {
  const Icon = iconMap[experience.icon];

  return (
    <div className="mb-12">
      <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg">
        {/* Header with title and description */}
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-primary rounded-full p-3">
              <Icon className="text-black" size={24} />
            </div>
            <h3 
              className="text-2xl sm:text-3xl font-bold text-black" 
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              {experience.title}
            </h3>
          </div>
          <p className="text-gray-700 leading-relaxed text-lg">
            {experience.description}
          </p>
        </div>

        {/* Integrated Carousel Gallery */}
        <div className="px-4 pb-6">
          <Carousel className="w-full">
            <CarouselContent>
              {galleryImages.map((image) => (
                <CarouselItem key={image.id} className={fullHeight ? "md:basis-1/2 lg:basis-1/2" : "md:basis-1/2 lg:basis-1/3"}>
                  <div className="relative overflow-hidden rounded-xl group" style={{ height: fullHeight ? '500px' : '240px' }}>
                    <img 
                      src={image.url}
                      alt={image.alt}
                      className={`w-full h-full ${fullHeight ? 'object-contain bg-gray-100' : 'object-cover'} group-hover:scale-110 transition-transform duration-500`}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-3">
                      <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 leading-snug">
                        {image.alt}
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
            <CarouselNext className="right-2 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
          </Carousel>
        </div>
      </div>
    </div>
  );
};

// Simple experience card without gallery (for Study Center)
const SimpleExperienceCard = ({ experience }) => {
  const Icon = iconMap[experience.icon];

  return (
    <div className="group relative bg-gray-50 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300">
      <div className="relative h-64 overflow-hidden">
        <img 
          src={experience.image}
          alt={experience.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 bg-white rounded-full p-3">
          <Icon className="text-black" size={24} />
        </div>
      </div>
      <div className="p-8">
        <h3 className="text-2xl font-bold text-black mb-3" style={{ fontFamily: "'Crimson Text', serif" }}>
          {experience.title}
        </h3>
        <p className="text-gray-700 leading-relaxed">
          {experience.description}
        </p>
      </div>
    </div>
  );
};

const Experiences = () => {
  // Get specific experiences
  const greenhouse = experiences.find(exp => exp.id === 2);
  const upick = experiences.find(exp => exp.id === 3);
  const gardens = experiences.find(exp => exp.id === 4);

  return (
    <section id="experiences" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            Explore Our Experiences
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Immerse yourself in hands-on learning across our diverse farm ecosystems. 
            Each experience is designed to educate, inspire, and connect you with sustainable living.
          </p>
        </div>

        {/* Greenhouses with integrated gallery - full height images */}
        <ExperienceCardWithGallery experience={greenhouse} galleryImages={greenhouseGalleryImages} fullHeight={true} />

        {/* U-Pick with integrated gallery - full height images */}
        <ExperienceCardWithGallery experience={upick} galleryImages={upickHarvestImages} fullHeight={true} />

        {/* Gardens Section - Carousel Style */}
        <div className="mb-12 bg-gray-50 rounded-2xl p-8 sm:p-12" id="garden-beauty">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="bg-black rounded-full p-3">
                <Flower2 className="text-white" size={24} />
              </div>
              <h3 
                className="text-3xl sm:text-4xl font-bold text-black"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                {gardens.title}
              </h3>
            </div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              {gardens.description}
            </p>
          </div>

          <div className="px-2 sm:px-8">
            <Carousel className="w-full">
              <CarouselContent>
                {gardenBeautyImages.map((image) => (
                  <CarouselItem key={image.id} className="md:basis-1/2 lg:basis-1/2">
                    <div 
                      className="relative overflow-hidden rounded-xl group cursor-pointer"
                      style={{ height: '500px' }}
                    >
                      <img 
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-6">
                        <p className="text-white text-base font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 leading-relaxed">
                          {image.alt}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-left-4 sm:left-0 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
              <CarouselNext className="-right-4 sm:right-0 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
            </Carousel>
          </div>
        </div>

        {/* Farm Moments Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h3 
              className="text-2xl sm:text-3xl font-bold text-black mb-3"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Moments from the Farm
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Discover the beauty of sustainable living through snapshots of our daily farm life.
            </p>
          </div>
          <div className="px-2 sm:px-8">
            <Carousel className="w-full">
              <CarouselContent>
                {farmMomentsImages.map((image) => (
                  <CarouselItem key={image.id} className="md:basis-1/2 lg:basis-1/3">
                    <div className="relative overflow-hidden rounded-xl group cursor-pointer" style={{ height: '280px' }}>
                      <img 
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-4">
                        <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {image.alt}
                        </p>
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-left-4 sm:left-0 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
              <CarouselNext className="-right-4 sm:right-0 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
            </Carousel>
          </div>
        </div>

        {/* Learn Permaculture CTA */}
        <div className="bg-black rounded-2xl p-8 sm:p-12 text-center">
          <h3 
            className="text-3xl sm:text-4xl font-bold text-primary mb-4"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            Learn Permaculture Principles
          </h3>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Our comprehensive approach to sustainable agriculture combines traditional wisdom with modern 
            techniques. Study ecosystem design, soil regeneration, water conservation, and biodiversity—all 
            while experiencing the beauty and abundance of working with nature, not against it.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Experiences;
